import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import ArabicLoader from '../components/ArabicLoader';
import { useAuth } from '../lib/auth';
import { THEME_META, THEME_TOPICS } from '../data/themes';
import { readUserLevel, readLearningTrack, getRecommendedThemesForUser, pickSurpriseTheme } from '../lib/theme-recommendations';
import { lsGet, lsSet, guestGetLatestProgress } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { useFeaturedPoem } from '../hooks/use-featured-poem';
import { prefetchPoem } from '../lib/prefetch';
import { useUserStats } from '../hooks/use-user-stats';
import PWAInstallHint from '../components/PWAInstallHint';
import StreakCard from '../components/StreakCard';
import StreakModal from '../components/StreakModal';
import { readStreak, tickStreak, getMilestone } from '../lib/streak';
import { TierDisplay } from '../lib/tiers.jsx';
import { trackEvent } from '../lib/analytics';
import TourOverlay from '../components/TourOverlay';
import HelpSheet from '../components/HelpSheet';
import ThemeToggle from '../components/ThemeToggle';
import SpotlightTour from '../components/SpotlightTour';
import WalletChip from '../components/WalletChip';
import WelcomeSheet, { shouldShowWelcome } from '../components/WelcomeSheet';

const HOME_HELP = [
  { icon: '📜', label: "Today's Bayt", desc: "A new verse every day. Tap it to open the full poem and start reading." },
  { icon: '🗺️', label: 'Your Path', desc: 'Theme-based reading tracks matched to your level. Tap any card to open a poem.' },
  { icon: '▶️', label: 'Continue Reading', desc: 'Picks up the last poem you were reading, at the exact verse you left off.' },
  { icon: '🔥', label: 'Streak', desc: 'Your daily reading habit. Opening any poem counts as a day toward your streak.' },
  { icon: '❓', label: 'This button', desc: 'Tap ? on any screen for a quick reminder of what everything does.' },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

function getHomePaths() {
  const level = readUserLevel();
  const track = readLearningTrack();
  return getRecommendedThemesForUser(level, track).slice(0, 3);
}

export default function Home() {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [navError, setNavError] = useState(false);

  const { featured, error: featuredError, regenerating, visible, regenerate } = useFeaturedPoem();
  const [userId, setUserId] = useState(() => lsGet('diwanPrismaUserId'));
  const { stats, progress, error: statsError, loading: statsLoading, refetch: refetchStats } = useUserStats(userId);

  // Pick up diwanPrismaUserId when AuthContext hydrates it after mount (incognito, new device)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'diwanPrismaUserId' && e.newValue) setUserId(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  function goAllPaths() {
    try { navigate('/all-paths'); }
    catch { setNavError(true); }
  }

  const [greeting, setGreeting] = useState(getGreeting);
  // Re-evaluate greeting when the app returns to foreground (tab/PWA resume)
  useEffect(() => {
    function update() { setGreeting(getGreeting()); }
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  const [modal, setModal] = useState(null);
  const [streak, setStreak] = useState(() => readStreak());
  const [helpOpen, setHelpOpen] = useState(false);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const streakRef          = useRef(null);
  const statsRef           = useRef(null);
  const continueReadingRef = useRef(null);
  const todaysBaytRef      = useRef(null);
  const ourStoryRef        = useRef(null);
  const supportRef         = useRef(null);
  const pathsRef           = useRef(null);
  const surpriseMeRef      = useRef(null);

  // Show welcome sheet once per account (first login or first visit after feature shipped).
  // Deferred by one tick so it doesn't fire synchronously inside the effect.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    if (!userId || !shouldShowWelcome()) return;
    const t = setTimeout(() => setShowWelcome(true), 600);
    return () => clearTimeout(t);
  }, [userId]);

  // Advance streak counter once per calendar day
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setStreak(tickStreak()); }, []);

  // Sync streak from server on mount so cross-device streak is up to date
  useEffect(() => {
    if (!userId) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      const token = session?.access_token;
      if (!token) return;
      const API = window.location.origin;
      fetch(`${API}/api/users/${userId}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && typeof data.streak === 'number') {
            setStreak(prev => {
              const serverCount    = data.streak;
              const serverBest     = data.bestStreak ?? 0;
              const merged = {
                ...prev,
                streak_count: serverCount,
                // Server best_streak is authoritative; fall back to local if higher
                // (covers users whose best was only tracked locally before this change)
                best_streak: Math.max(prev.best_streak ?? 0, serverBest, serverCount),
              };
              try { localStorage.setItem('diwanStreak', JSON.stringify(merged)); } catch { /* storage blocked */ }
              return merged;
            });
          }
        })
        .catch(() => {});
    });
  }, [userId]);

  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(() => lsGet('diwanLearningTrack') || '');

  const [displayName, setDisplayName] = useState(() => lsGet('diwanDisplayName') || '');
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);

  async function saveDisplayName() {
    const trimmed = editNameValue.trim();
    setEditingName(false);
    if (!trimmed || trimmed === displayName) return;
    lsSet('diwanDisplayName', trimmed);
    setDisplayName(trimmed);
    const prismaUserId = lsGet('diwanPrismaUserId');
    if (prismaUserId) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        await fetch(`/api/users/${prismaUserId}/settings`, {
          method: 'PATCH', headers,
          body: JSON.stringify({ name: trimmed }),
        });
      } catch { /* non-fatal */ }
    }
  }

  async function handleTrackChange(newTrack) {
    lsSet('diwanLearningTrack', newTrack);
    setCurrentTrack(newTrack);
    setTrackModalOpen(false);
    const prismaUserId = lsGet('diwanPrismaUserId');
    if (prismaUserId) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        await fetch(`/api/users/${prismaUserId}/settings`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ learningTrack: newTrack }),
        });
      } catch { /* non-fatal */ }
    }
  }

  // If a password-recovery email delivered the user here (Supabase SITE_URL
  // misconfigured to /home), redirect to the dedicated reset form.
  useEffect(() => {
    if (sessionStorage.getItem('diwan_recovery_pending') === 'true') {
      navigate('/reset-password', { replace: true });
    }
  }, [navigate]);

  // Auto-fire trigger overlay when at a milestone (once per milestone)
  useEffect(() => {
    const { streak_count } = streak;
    if ([7, 30, 100, 365].includes(streak_count)) {
      const key = `milestone_shown_${streak_count}`;
      if (!localStorage.getItem(key)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setModal({ type: 'trigger', data: getMilestone(streak_count) });
      }
    }
  }, []);

  function handleOpenModal(state, data) {
    setModal({ type: state, data });
  }

  function handleCloseModal() {
    if (modal?.type === 'trigger') {
      localStorage.setItem(`milestone_shown_${streak.streak_count}`, 'true');
    }
    setModal(null);
  }

  const liveStats = [
    { num: String(stats.baytsRead),   label: 'Bayts Read',   onClick: () => navigate('/all-paths') },
    { num: String(stats.wordsSaved),  label: 'Words Saved',  onClick: () => navigate('/words') },
    { num: String(stats.poemsSaved),  label: 'Poems Saved',  onClick: () => navigate('/saved') },
    { num: String(stats.cardsDue),    label: 'Cards Due',    onClick: () => navigate('/words', { state: { tab: 'flash' } }), due: stats.cardsDue > 0 },
  ];

  const featuredVerseLines = featured?.verseText?.split(' · ') ?? [];

  // Resume-reading data — computed once, consumed in two places
  let resumeLatest = null;
  let resumeIsGuest = false;
  if (userId && progress.length) {
    const inProgress = progress.filter(p => p.status === 'READING' && p.poem);
    if (inProgress.length) {
      resumeLatest = {
        poemId:      inProgress[0].poemId,
        poemTitleAr: inProgress[0].poem.titleAr,
        poetAr:      inProgress[0].poem.poetAr,
        verseIdx:    inProgress[0].currentVerse,
        extraCount:  inProgress.length - 1,
      };
    }
  }
  if (!resumeLatest) {
    const guest = guestGetLatestProgress();
    if (guest && guest.verseIdx > 0) {
      resumeLatest = { poemId: guest.poemId, poemTitleAr: guest.poemTitleAr, poetAr: guest.poetAr, verseIdx: guest.verseIdx, extraCount: 0 };
      resumeIsGuest = true;
    }
  }

  const [themeCounts, setThemeCounts] = useState({});
  useEffect(() => {
    const API = window.location.origin;
    // Only fetch theme data when the Your Path section scrolls into view
    // If IntersectionObserver is not supported, fetch immediately
    const el = document.getElementById('home-paths-section');
    if (!el || !window.IntersectionObserver) {
      fetch(`${API}/api/poems/browse?mode=init`)
        .then(r => r.json())
        .then(data => {
          const counts = {};
          Object.entries(data.themeCounts ?? {}).forEach(([id, total]) => {
            counts[id] = total;
          });
          setThemeCounts(counts);
        })
        .catch(() => {});
      return;
    }

    let fetched = false;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !fetched) {
          fetched = true;
          observer.disconnect();
          fetch(`${API}/api/poems/browse?mode=init`)
            .then(r => r.json())
            .then(data => {
              const counts = {};
              Object.entries(data.themeCounts ?? {}).forEach(([id, total]) => {
                counts[id] = total;
              });
              setThemeCounts(counts);
            })
            .catch(() => {});
        }
      },
      { rootMargin: '200px' }  // start fetching 200px before visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Compute fractional bayt-based progress per theme
  const topicToTheme = {};
  Object.entries(THEME_TOPICS).forEach(([id, topics]) =>
    topics.forEach(t => { topicToTheme[t] = id; })
  );
  const studiedByTheme   = {};  // whole poems completed
  const progressByTheme  = {};  // fractional sum: each poem contributes 0–1 based on bayts read
  const readCountByTheme = {};  // poems started or completed (for tier system)
  for (const p of progress) {
    const themeId = topicToTheme[p.poem?.topic];
    if (!themeId) continue;
    const verseCount = p.poem?.verseCount || 0;
    let fraction;
    if (p.status === 'STUDIED') {
      fraction = 1.0;
      studiedByTheme[themeId]   = (studiedByTheme[themeId]   || 0) + 1;
      readCountByTheme[themeId] = (readCountByTheme[themeId] || 0) + 1;
    } else if (p.status === 'READING' && verseCount > 0) {
      fraction = Math.min(1, (p.currentVerse + 1) / verseCount);
      readCountByTheme[themeId] = (readCountByTheme[themeId] || 0) + 1;
    } else {
      fraction = 0;
    }
    if (fraction > 0) progressByTheme[themeId] = (progressByTheme[themeId] || 0) + fraction;
  }
  const totalStudied = progress.filter(p => p.status === 'STUDIED').length;

  const themePaths = getHomePaths().map(key => {
    const meta         = THEME_META[key];
    const studied      = studiedByTheme[key]   || 0;
    const readCount    = readCountByTheme[key] || 0;
    const progress_sum = progressByTheme[key]  || 0;
    const total        = themeCounts[key] || 0;
    const pct          = total > 0 ? Math.min(100, Math.round((progress_sum / total) * 100)) : 0;
    return { key, icon: meta?.icon, name: meta?.nameEn, level: meta?.level, studied, readCount, total, pct, started: progress_sum > 0 };
  });

  return (
    <div className="phone-shell">
      {!tourActive && <div className="accent-bar" />}

      <div className="screen-content" style={tourActive ? { visibility: 'hidden' } : undefined}>

        {/* ── Header ── */}
        <div style={{ padding: '1.25rem 1.5rem 1.25rem', zIndex: 1 }}>

          {/* مرحباً بك row + buttons — flex, no absolute positioning */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--em)', opacity: 0.6 }}>مرحباً بك</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <WalletChip />
              <ThemeToggle inline />
              {!authLoading && user && (
                <button
                  onClick={() => setProfileOpen(v => !v)}
                  aria-label="Account"
                  style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: profileOpen ? 'var(--em-soft)' : 'var(--bg2)',
                    border: `1px solid ${profileOpen ? 'var(--em)' : 'var(--border)'}`,
                    color: profileOpen ? 'var(--em)' : 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem',
                    fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    touchAction: 'manipulation',
                  }}
                >
                  {(displayName || user.email || '?')[0].toUpperCase()}
                </button>
              )}
              <button
                className="question-btn"
                onClick={() => setSpotlightOpen(true)}
                aria-label="Help"
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  border: '1px solid var(--border)', background: 'var(--bg2)',
                  color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: '0.65rem', cursor: 'pointer', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  touchAction: 'manipulation',
                }}
              >?</button>
            </div>
          </div>

          <div style={{ fontSize: '1.6rem', color: 'var(--text)', fontStyle: 'italic', marginBottom: '0.35rem' }}>
            {greeting}{user ? (
              editingName ? (
                <>, <input
                  type="text"
                  value={editNameValue}
                  onChange={e => setEditNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') setEditingName(false); }}
                  onBlur={saveDisplayName}
                  autoFocus
                  style={{
                    background: 'none', border: 'none', borderBottom: '1px solid var(--em)',
                    color: 'var(--text)', fontStyle: 'italic', fontSize: '1.6rem',
                    fontFamily: 'inherit', padding: 0, outline: 'none', width: '8rem',
                  }}
                /></>
              ) : displayName ? `, ${displayName.split(' ')[0]}` : ''
            ) : ''}
          </div>

          {/* Profile panel — shown when profile icon is tapped */}
          {!authLoading && user && profileOpen && (
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.6rem',
              animation: 'fadeUp 0.15s ease',
            }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.6rem', wordBreak: 'break-all' }}>
                {user.email}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                <button
                  onClick={() => { setEditNameValue(displayName); setEditingName(true); setProfileOpen(false); }}
                  style={{ background: 'none', border: 'none', padding: '0.4rem 0', cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text2)', textAlign: 'left' }}
                >Edit name →</button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0.3rem 0' }} />
                <button
                  onClick={() => { setProfileOpen(false); signOut(); }}
                  style={{ background: 'none', border: 'none', padding: '0.4rem 0', cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(239,100,80,0.85)', textAlign: 'left' }}
                >Sign out</button>
              </div>
            </div>
          )}

          {!authLoading && !user && (
            <div
              onClick={() => navigate('/splash')}
              style={{
                marginTop: '0.5rem', marginBottom: '0.4rem', cursor: 'pointer',
                background: 'rgba(140,207,225,0.06)',
                border: '1px solid rgba(140,207,225,0.25)',
                borderLeft: '3px solid rgba(140,207,225,0.6)',
                borderRadius: 4, padding: '0.6rem 0.9rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text)', fontWeight: 600 }}>
                  Sign in to save your progress
                </div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.06em', color: 'var(--text3)', marginTop: '0.2rem' }}>
                  Sync streak · words · reading history
                </div>
              </div>
              <span style={{ color: 'var(--em)', fontSize: '1.1rem', marginLeft: '0.75rem' }}>→</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 0 }}>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.7rem', letterSpacing: '0.1em', color: 'var(--gold)', textTransform: 'uppercase' }}>
              {currentTrack || lsGet('diwanArabicLevel') || 'Classical Track'}
            </div>
            {lsGet('diwanUserLevel') && (
              <span style={{
                fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--bg)', background: 'var(--em)',
                borderRadius: 3, padding: '0.1rem 0.4rem',
              }}>
                {lsGet('diwanUserLevel')}
              </span>
            )}
            <button
              onClick={() => setTrackModalOpen(true)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)' }}
            >Change →</button>
          </div>

          <div ref={streakRef}><StreakCard streak={streak} onOpenModal={handleOpenModal} /></div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '0 1.5rem 2rem', position: 'relative', zIndex: 1 }}>

          {/* Quick stats */}
          {statsError && (
            <button
              onClick={refetchStats}
              role="alert"
              style={{
                display: 'block', width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', color: 'rgba(239,68,68,0.8)', marginBottom: '0.75rem', textAlign: 'center',
              }}
            >
              Couldn't load your progress — tap to retry.
            </button>
          )}
          <div ref={statsRef} style={{
            display: 'flex',
            background: 'var(--bg2)',
            border: '1px solid var(--border2)',
            borderRadius: 6,
            marginBottom: '1.75rem',
            overflow: 'hidden',
          }}>
            {liveStats.map((s, i) => (
              <div
                key={s.label}
                onClick={s.onClick}
                style={{
                  flex: 1,
                  padding: '0.85rem 0.25rem',
                  textAlign: 'center',
                  borderLeft: i > 0 ? '1px solid var(--border2)' : 'none',
                  background: s.due ? 'var(--gold-subtle)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                {statsLoading
                  ? <span className="skeleton" style={{ display: 'inline-block', width: 28, height: 16, borderRadius: 3, marginBottom: 4 }} />
                  : <span className="qs-num" style={s.due ? { color: 'var(--gold)' } : {}}>{s.num}</span>
                }
                <span className="qs-label">{s.label}</span>
                {s.due && <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.44rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--em)', display: 'block', marginTop: '0.2rem' }}>Review →</span>}
              </div>
            ))}
          </div>

          {/* ── Continue Reading ── */}
          {!resumeLatest && !authLoading && user && (
            <div style={{ marginBottom: '1.75rem' }}>
              <div className="home-section-title"><span>Continue Reading</span></div>
              <div style={{ padding: '0.9rem 1.1rem', border: '1px dashed var(--border)', borderRadius: 4, background: 'var(--bg2)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontStyle: 'italic', lineHeight: 1.6 }}>
                  Open a poem to start reading — your progress will appear here.
                </div>
              </div>
            </div>
          )}
          {resumeLatest && (
            <div ref={continueReadingRef} style={{ marginBottom: '1.75rem' }}>
              <div className="home-section-title"><span>Continue Reading</span></div>
              <div
                onClick={() => navigate(`/poem/${resumeLatest.poemId}`, { state: { resumeVerse: resumeLatest.verseIdx } })}
                style={{
                  padding: '0.9rem 1.1rem',
                  border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--em)',
                  borderRadius: 4,
                  background: 'var(--bg2)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', color: 'var(--text)', direction: 'rtl', textAlign: 'right', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {resumeLatest.poemTitleAr}
                  </div>
                  <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                    {resumeLatest.poetAr} · Bayt {resumeLatest.verseIdx + 1}
                  </div>
                  {resumeLatest.extraCount > 0 && (
                    <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', marginTop: '0.2rem' }}>
                      +{resumeLatest.extraCount} more in progress
                    </div>
                  )}
                  {resumeIsGuest && (
                    <div
                      onClick={e => { e.stopPropagation(); navigate('/splash'); }}
                      style={{ marginTop: '0.4rem', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer' }}
                    >
                      Reading on another device?{' '}
                      <span style={{ color: 'var(--em)' }}>Save your place →</span>
                    </div>
                  )}
                </div>
                <span style={{ color: 'var(--em)', fontSize: '1rem', flexShrink: 0, marginLeft: '0.75rem' }}>→</span>
              </div>
            </div>
          )}

          {/* ── Today's Bayt ── */}
          <div ref={todaysBaytRef} style={{ marginBottom: '1.75rem' }}>
            <div className="home-section-title">
              <span>Today's Bayt</span>
            </div>

            {featuredError ? (
              <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: 4 }}>
                Today's verse is hiding — check your connection.
              </div>
            ) : !featured ? (
              <div className="featured-card" aria-busy="true" aria-label="Loading today's poem" style={{ opacity: 0.4 }}>
                <div style={{ height: '0.6rem', background: 'var(--border)', borderRadius: 3, width: '40%', marginBottom: '1rem' }} />
                <div style={{ height: '1.4rem', background: 'var(--border)', borderRadius: 3, marginBottom: '0.5rem' }} />
                <div style={{ height: '1.4rem', background: 'var(--border)', borderRadius: 3, width: '75%', margin: '0 auto 1rem' }} />
                <div style={{ height: '0.7rem', background: 'var(--border)', borderRadius: 3, width: '55%' }} />
                <ArabicLoader message="Finding your poem…" />
              </div>
            ) : featured.dbError ? (
              <div style={{ padding: '1.25rem', textAlign: 'center', color: 'rgba(239,68,68,0.8)', fontStyle: 'italic', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: 4 }}>
                <div>The library is momentarily closed — check your connection.</div>
                {featured.dbErrorDetail && (
                  <div style={{ fontSize: '0.7rem', marginTop: '0.4rem', wordBreak: 'break-all', opacity: 0.8 }}>
                    {featured.dbErrorDetail}
                  </div>
                )}
                {featured.dbUrlSnippet && (
                  <div style={{ fontSize: '0.65rem', marginTop: '0.3rem', wordBreak: 'break-all', opacity: 0.6 }}>
                    URL: {featured.dbUrlSnippet}
                  </div>
                )}
              </div>
            ) : !featured.poemId ? (
              <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: 4 }}>
                No poems in the database yet.
              </div>
            ) : (
              <div
                className="featured-card"
                onClick={() => navigate(`/poem/${featured.poemId}`)}
                onMouseEnter={() => featured?.poemId && prefetchPoem(featured.poemId)}
                onFocus={() => featured?.poemId && prefetchPoem(featured.poemId)}
                style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
              >
                <div className="featured-card-glow" />
                <div className="fc-label">
                  <span style={{ fontSize: '0.4rem', color: 'var(--gold)', lineHeight: 1 }}>◆</span>
                  {featured.poetAr}
                </div>
                <div className="fc-arabic">
                  {featuredVerseLines[0]}<br />
                  {featuredVerseLines[1] || ''}
                </div>
                <div className="fc-poet">
                  <span>{featured.poetAr}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                      onClick={() => navigate(`/poem/${featured.poemId}`)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--gold)', fontFamily: 'inherit', fontSize: 'inherit' }}
                    >Study →</button>
                    {!regenerating && (
                      <button
                        onClick={e => { e.stopPropagation(); regenerate(); }}
                        aria-label="Try another verse"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif",
                          fontSize: '0.75rem', lineHeight: 1,
                          width: 34, height: 34,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, touchAction: 'manipulation',
                          borderRadius: '50%',
                        }}
                      >↺</button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Our Story ── */}
          <div ref={ourStoryRef} style={{ marginBottom: '1.25rem' }}>
            <div className="home-section-title">
              <span>Our Story</span>
            </div>
            <div
              onClick={() => { trackEvent('home_why_we_exist_clicked'); navigate('/premium', { state: { tab: 'why' } }); }}
              style={{
                padding: '1rem 1.1rem',
                border: '1px solid var(--border)',
                borderLeft: '3px solid var(--gold)',
                borderRadius: 4,
                background: 'var(--bg2)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.3rem' }}>
                  A Founder's Note
                </div>
                <div style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: '0.88rem', fontStyle: 'italic', color: 'var(--text)', marginBottom: '0.2rem' }}>
                  Arabic poetry is one of the richest parts of our heritage
                </div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', color: 'var(--text3)', letterSpacing: '0.04em', lineHeight: 1.6 }}>
                  No investors · no ads · built by a learner, for learners
                </div>
              </div>
              <span style={{ color: 'var(--gold)', fontSize: '1rem', flexShrink: 0, marginLeft: '0.75rem' }}>→</span>
            </div>
          </div>

          {/* ── Premium upsell ── */}
          {!user?.isPremium && (
            <div
              ref={supportRef}
              onClick={() => { trackEvent('home_support_clicked'); navigate('/premium'); }}
              style={{
                marginBottom: '1.25rem',
                padding: '1.1rem 1.2rem',
                border: '1px solid rgba(229,150,83,0.35)',
                borderTop: '2px solid var(--gold)',
                borderRadius: 8,
                background: 'linear-gradient(160deg, rgba(229,150,83,0.11) 0%, rgba(229,150,83,0.03) 100%)',
                boxShadow: '0 4px 24px rgba(229,150,83,0.14)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.35rem' }}>
                  ◆ Support the Dīwān
                </div>
                <div style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--text)', marginBottom: '0.25rem' }}>
                  11,009 poems · help us reach 50,000
                </div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', color: 'var(--text3)', letterSpacing: '0.04em', lineHeight: 1.6 }}>
                  Every poem you fund is analysed &amp; cached forever<br />
                  Open to all · no features locked
                </div>
              </div>
              <span style={{ color: 'var(--gold)', fontSize: '1.1rem', flexShrink: 0, marginLeft: '0.75rem' }}>→</span>
            </div>
          )}


          {/* ── Learning paths ── */}
          <div ref={pathsRef}>
            <div id="home-paths-section" className="home-section-title">
              <span>Your Path</span>
              <button className="see-all" onClick={goAllPaths}>all paths →</button>
            </div>
            {navError && (
              <div role="alert" style={{ fontSize: '0.75rem', color: 'rgba(239,68,68,0.8)', marginBottom: '0.5rem' }}>
                Couldn't open the paths — try again.
              </div>
            )}

            {themePaths.map(path => (
              <div key={path.key} className="path-card" onClick={() => navigate(`/theme/${path.key}`)}
                style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: '1.1rem 1.25rem' }}>
                {/* Row 1: icon + name + level */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.35rem' }}>
                  <div className="path-icon" style={{ fontSize: '1.15rem', flexShrink: 0 }}>{path.icon}</div>
                  <div style={{ flex: 1 }}>
                    <span className="path-name">{path.name}</span>
                  </div>
                  <span className="tag tag-gold" style={{ flexShrink: 0 }}>{path.level}</span>
                </div>
                {/* Row 2+3: tier ring + counter + near-miss */}
                <TierDisplay readCount={path.readCount} pathKey={path.key} />
              </div>
            ))}

            {totalStudied > 0 && (
              <div style={{ textAlign: 'center', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginTop: '0.75rem' }}>
                {totalStudied} poem{totalStudied !== 1 ? 's' : ''} studied total
              </div>
            )}

            {/* Surprise me */}
            <div
              ref={surpriseMeRef}
              onClick={() => navigate(`/theme/${pickSurpriseTheme(readUserLevel())}`)}
              style={{ marginTop: '0.65rem', background: 'linear-gradient(135deg, rgba(229,150,83,0.07), rgba(140,207,225,0.06))', border: '1px solid rgba(229,150,83,0.24)', borderRadius: 4, padding: '0.85rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
            >
              <span style={{ fontSize: '1.1rem' }}>✨</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)', fontWeight: 600 }}>Surprise me</div>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', marginTop: '0.1rem' }}>Opens a theme pitched to your level — or beyond</div>
              </div>
              <span style={{ color: 'var(--gold)', fontSize: '1rem' }}>→</span>
            </div>
          </div>

          {/* Feedback & Bug Report */}
          <div style={{
            marginTop: '1.25rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={() => setShowWelcome(true)}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '0.5rem 1rem',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.6rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--gold)',
                cursor: 'pointer',
              }}
            >About Al-Diwan</button>
            <button
              onClick={() => navigate('/feedback', { state: { screen: 'Home', type: 'feedback' } })}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '0.5rem 1rem',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.6rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--text2)',
                cursor: 'pointer',
              }}
            >Share Feedback</button>
            <button
              onClick={() => navigate('/feedback', { state: { screen: 'Home', type: 'bug' } })}
              style={{
                background: 'none',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 4,
                padding: '0.5rem 1rem',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.6rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(239,68,68,0.7)',
                cursor: 'pointer',
              }}
            >Report a Bug</button>
            {!spotlightOpen && !tourActive && <PWAInstallHint />}
          </div>
        </div>
      </div>

      <StreakModal modal={modal} streak={streak} onClose={handleCloseModal} />
      {showWelcome && <WelcomeSheet onClose={() => setShowWelcome(false)} />}
      <TourOverlay onVisibilityChange={setTourActive} />
      {spotlightOpen && (
        <SpotlightTour
          onDone={() => setSpotlightOpen(false)}
          steps={[
            {
              ref: streakRef,
              title: 'Streak',
              desc: 'Your daily reading habit. Opening any poem counts as a day — keep the flame alive and track your milestone journey.',
            },
            {
              ref: statsRef,
              title: 'Reading Stats',
              desc: 'Bayts read, words saved, poems saved, and flashcards due. Tap any number to jump straight to that section.',
            },
            {
              ref: continueReadingRef,
              title: 'Continue Reading',
              desc: 'Picks up the last poem you were reading at the exact bayt you left off — tap to jump straight back in.',
            },
            {
              ref: todaysBaytRef,
              title: "Today's Bayt",
              desc: 'A new verse chosen for you each day. Tap the card to open the full poem and start reading, or hit Regenerate for a different one.',
            },
            {
              ref: ourStoryRef,
              title: 'Our Story',
              desc: 'A note from the founder on why Al-Diwan exists — no investors, no ads, built by a learner for learners.',
            },
            ...(!user?.isPremium ? [{
              ref: supportRef,
              title: 'Support the Dīwān',
              desc: 'Every contribution funds more poems — fully analysed and cached forever. No features are locked; everything stays open to all readers.',
              ctaLabel: 'Support the Dīwān →',
              ctaAction: () => navigate('/premium'),
            }] : []),
            {
              ref: pathsRef,
              title: 'Your Path',
              desc: 'Three reading tracks matched to your level and chosen theme. Tap any card to open a poem in that strand.',
            },
            {
              ref: surpriseMeRef,
              title: 'Surprise me',
              desc: 'Opens a random theme pitched to your level or above — good for when you want to be taken somewhere new without choosing.',
            },
          ]}
        />
      )}
      {helpOpen && <HelpSheet title="What's on this screen" items={HOME_HELP} onClose={() => setHelpOpen(false)} />}

      {trackModalOpen && (
        <div
          onClick={() => setTrackModalOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '1.5rem', width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '1rem' }}>
              Choose your learning track
            </div>
            {[
              ['Foundations', 'Beginner — short, accessible verses',  '🌿'],
              ['Fluency',     'Building reading speed & rhythm',       '🌊'],
              ['Classical',   'Pre-Islamic & Abbasid canon',           '📜'],
              ['Modernist',   'Modern angles on classical texts',      '✨'],
              ['Mixed Path',  'Balanced, no theme priority',           '⚖️'],
              ['Scholars',    'Dense, complex — for advanced readers', '📚'],
            ].map(([track, desc, emoji]) => (
              <div
                key={track}
                onClick={() => handleTrackChange(track)}
                style={{
                  padding: '0.75rem 0.9rem', marginBottom: '0.4rem', cursor: 'pointer', borderRadius: 4,
                  border: currentTrack === track ? '1px solid var(--gold-line)' : '1px solid var(--border)',
                  background: currentTrack === track ? 'rgba(229,150,83,0.08)' : 'var(--bg3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                  <span style={{ fontSize: '1.3rem', lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
                  <div>
                    <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.85rem', letterSpacing: '0.06em', color: currentTrack === track ? 'var(--gold)' : 'var(--text)' }}>
                      {track}
                    </div>
                    <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.05em', color: 'var(--text3)', marginTop: '0.15rem' }}>
                      {desc}
                    </div>
                  </div>
                </div>
                {currentTrack === track && (
                  <span style={{ color: 'var(--gold)', fontSize: '0.8rem' }}>✓</span>
                )}
              </div>
            ))}
            <button
              onClick={() => setTrackModalOpen(false)}
              style={{ width: '100%', marginTop: '0.5rem', padding: '0.55rem', background: 'none', border: '1px solid var(--border)', borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer' }}
            >Cancel</button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
